import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Users, TrendingUp, Award, Wrench, Target, ArrowRight, ArrowLeft, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HOOK_ICONS = [Users, TrendingUp, Award, Wrench, Target];
const HOOK_COLORS = [
  "from-emerald-500/10 to-transparent",
  "from-lime-500/10 to-transparent",
  "from-teal-500/10 to-transparent",
  "from-green-500/10 to-transparent",
  "from-cyan-500/10 to-transparent",
];

export function Step2HookLayer({ companyId, onComplete, onBack }) {
  const [hooks, setHooks] = useState([]);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHooks = async () => {
      try {
        const { data } = await axios.post(`${API}/hooks/generate`, { company_id: companyId });
        setHooks(data.hooks || []);
      } catch (err) {
        toast.error("Could not load opportunity insights.");
      } finally {
        setLoading(false);
      }
    };
    if (companyId) fetchHooks();
  }, [companyId]);

  const toggleHook = (index) => {
    setSelected((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const handleContinue = async () => {
    try {
      await axios.post(`${API}/hooks/select`, {
        company_id: companyId,
        selected_hooks: selected,
      });
      onComplete();
    } catch (err) {
      toast.error("Could not save selection.");
    }
  };

  if (loading) {
    return (
      <div className="space-y-8">
        <div>
          <p className="text-sm text-muted-foreground uppercase tracking-widest mb-2">Analyzing opportunities</p>
          <h2 className="text-3xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Finding what matters for you...
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={`skel-${i}`} className="glass-card h-36 animate-pulse" style={{ animationDelay: `${i * 150}ms` }} />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <p className="text-sm text-muted-foreground uppercase tracking-widest mb-2">Opportunities</p>
        <h2 className="text-3xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Which of these already apply to you?
        </h2>
        <p className="text-base text-muted-foreground mt-3 leading-relaxed">
          Tap every card your company is already doing — this personalizes your roadmap.
        </p>
        <div className="mt-2 inline-flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-full px-3 py-1">
          <Check className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs text-primary font-medium">{selected.length} of {hooks.length} selected</span>
        </div>
      </div>

      <div className="space-y-3">
        {hooks.map((hook, i) => {
          const Icon = HOOK_ICONS[i % HOOK_ICONS.length];
          const isSelected = selected.includes(i);
          return (
            <motion.div
              key={`hook-${hook?.slice(0,20)}-${i}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.07 }}
              onClick={() => toggleHook(i)}
              className={`glass-card p-4 cursor-pointer transition-all duration-300 flex items-center gap-4 ${isSelected ? 'glass-card-selected' : ''}`}
              data-testid={`hook-card-${i}`}
            >
              <div className={`absolute inset-0 bg-gradient-to-r ${HOOK_COLORS[i % HOOK_COLORS.length]} opacity-30 pointer-events-none`} />
              <div className="relative flex items-center gap-4 flex-1 min-w-0">
                <div className={`w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center ${isSelected ? 'bg-primary/20' : 'bg-white/5'} transition-colors`}>
                  <Icon className={`w-5 h-5 ${isSelected ? 'text-primary' : 'text-white/50'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-white">{hook.title}</h3>
                  <p className="text-sm text-white/50 leading-relaxed truncate md:whitespace-normal">{hook.description}</p>
                </div>
                <div className={`w-6 h-6 rounded-full flex-shrink-0 flex items-center justify-center border-2 transition-all duration-300 ${isSelected ? 'bg-primary border-primary' : 'border-white/20'}`}>
                  {isSelected && (
                    <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }}>
                      <Check className="w-3.5 h-3.5 text-primary-foreground" />
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className="flex items-center gap-3">
        {onBack && (
          <Button
            data-testid="hooks-back-btn"
            onClick={onBack}
            variant="ghost"
            className="rounded-full px-5 h-14 text-muted-foreground hover:text-white text-lg"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </Button>
        )}
        <Button
          data-testid="hooks-continue-btn"
          onClick={handleContinue}
          className="rounded-full px-8 h-14 bg-primary text-primary-foreground text-lg font-medium btn-glow"
        >
          Continue
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
      </div>
    </div>
  );
}

export default Step2HookLayer;
